/**
 * One "Fazly vs X" page.
 *
 * ── The block order is the citation shape ────────────────────────────────────
 *
 * H1 → standfirst → answer capsule → the two "best for" cards → what they do
 * better → the dimension table → the caveat → FAQ → the other comparisons.
 *
 * The answer capsule comes before the table because that is the block written
 * to be extracted, and the two "best for" cards come before the table because
 * they are the answer a reader actually came for: a comparison page's job is to
 * end a decision, and a table asks the reader to make it themselves.
 *
 * "What they do better" is rendered with the same weight as our own card, not
 * as a footnote. A comparison page that finds nothing good to say about the
 * alternative is an advertisement, and both readers and assistants discount it
 * as one.
 */

import type { ContentLang } from "@fazly/shared/contentLang";
import { Link } from "@tanstack/react-router";

import { COMPARE_SLUGS, comparePath } from "../../lib/compare/index";
import type { CompareCopy, CompareSlug } from "../../lib/compare/types";
import type { CompareUi } from "../../lib/compare/ui";
import { toolsIndexPath } from "../../lib/tools/index";
import { ContentFooter } from "../ContentFooter";

export function ComparePage({
  slug,
  copy,
  ui,
  lang,
  allCopy,
}: {
  slug: CompareSlug;
  copy: CompareCopy;
  ui: CompareUi;
  lang: ContentLang;
  allCopy: Record<CompareSlug, CompareCopy>;
}) {
  const others = COMPARE_SLUGS.filter((other) => other !== slug);

  return (
    <article className="mx-auto w-full max-w-3xl px-6 py-12 sm:py-16">
      <h1 className="font-display text-[clamp(2rem,7vw,3rem)] leading-[1.1] font-bold tracking-tight text-ink">
        {copy.h1}
      </h1>
      <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink2">
        {copy.standfirst}
      </p>

      <p className="mt-8 text-[17px] leading-[1.7] text-body">{copy.answer}</p>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.bestForHeading}
        </h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="brutal p-6">
            <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
              {ui.chooseFazly}
            </p>
            <p className="mt-3 leading-relaxed text-body">
              {copy.bestForFazly}
            </p>
          </div>
          <div className="brutal p-6">
            <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
              {ui.chooseRival.replace("%s", copy.rival)}
            </p>
            <p className="mt-3 leading-relaxed text-body">
              {copy.bestForRival}
            </p>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.theyDoBetter}
        </h2>
        <p className="mt-4 text-[17px] leading-[1.7] text-body">
          {copy.theirStrength}
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.tableHeading}
        </h2>
        {/* The table scrolls inside its own container rather than widening the
            page: five dimensions with a paragraph each is wider than a phone,
            and a body that scrolls sideways is the classic mobile bug. */}
        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[34rem] border-collapse text-left">
            <thead>
              <tr className="border-b-[2.5px] border-ink">
                <th className="w-[38%] py-3 pr-4 font-display text-sm font-bold text-ink">
                  {ui.dimensionColumn}
                </th>
                <th className="py-3 font-display text-sm font-bold text-ink">
                  {ui.fazlyColumn}
                </th>
              </tr>
            </thead>
            <tbody>
              {copy.rows.map((row) => (
                <tr key={row.dimension} className="border-b border-hair">
                  <th
                    scope="row"
                    className="py-4 pr-4 align-top font-display text-[15px] font-bold text-ink"
                  >
                    {row.dimension}
                  </th>
                  <td className="py-4 align-top text-[15px] leading-relaxed text-body">
                    {row.fazly}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-5 border-l-[3px] border-brand pl-4 text-[15px] leading-relaxed text-ink2">
          {copy.caveat}
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.faqHeading}
        </h2>
        {/* Questions as <h3>, not <dt> — same reason as ToolPage: a heading is
            what an extractor splits on and lifts as the question being answered,
            and a <dt> may not contain one. */}
        <div className="mt-4 flex flex-col gap-6">
          {copy.faq.map((item) => (
            <div key={item.q}>
              <h3 className="font-display text-lg font-bold text-ink">
                {item.q}
              </h3>
              <p className="mt-2 text-[17px] leading-[1.7] text-body">
                {item.a}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 border-t-[2.5px] border-ink pt-8">
        <p className="text-[17px] leading-relaxed text-body">{ui.tryFirst}</p>
        <Link
          to={toolsIndexPath(lang)}
          className="rim mt-4 inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
        >
          {ui.toolsCta}
        </Link>

        <h2 className="mt-10 font-display text-xl font-bold tracking-tight text-ink">
          {ui.otherComparisons}
        </h2>
        <ul className="mt-3 flex flex-col gap-2">
          {others.map((other) => (
            <li key={other}>
              <Link
                to={comparePath(other, lang)}
                className="font-display font-bold text-brand underline decoration-hair underline-offset-4 hover:decoration-brand"
              >
                {allCopy[other].h1}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <ContentFooter lang={lang} />
    </article>
  );
}
